/**
 * `<DemoScenarioButton/>` - the demo-only "Change the scenario" button in the
 * top bar (#2824). It is marked "Demo only", so a visitor never takes it for
 * part of the product. It stays closed until a solve is done. Then it opens a
 * dialog with the stored changes. Picking one rebuilds the team and starts
 * that scenario's tour.
 */
import { RotateCcw } from "lucide-react";
import { useId, useState } from "react";

import { useApplyDemoChange } from "../../api/useDemoChange";
import { useDemoSessionState } from "../../api/useDemoSessionState";
import { cn } from "../../lib/cn";
import { readDemoSession } from "../../lib/demoSession";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui";
import { DemoChangePicker } from "./DemoChangePicker";
import { setDemoGuideScenarioPending, startDemoGuideScenario } from "./guide/events";

const LOCKED_TEXT = "You need to complete a solve first";

export function DemoScenarioButton() {
  const session = readDemoSession();
  const state = useDemoSessionState();
  const [open, setOpen] = useState(false);
  const [tip, setTip] = useState(false);
  const tipId = useId();
  const apply = useApplyDemoChange((resp) => {
    setOpen(false);
    if (resp.variant) startDemoGuideScenario(resp.variant);
    else setDemoGuideScenarioPending(false);
  });
  const changes = state.data?.changes ?? [];
  if (!session || changes.length === 0) return null;
  const ready = state.data?.has_solved === true;
  const current = state.data?.variant ?? session.variant ?? null;
  return (
    <div className="relative flex shrink-0">
      <button
        type="button"
        aria-label="Change the scenario (demo only)"
        aria-disabled={ready ? undefined : "true"}
        aria-describedby={ready ? undefined : tipId}
        data-testid="demo-scenario-open"
        data-tour="nav-change-scenario"
        onClick={() => {
          if (ready) setOpen(true);
        }}
        onMouseEnter={() => setTip(true)}
        onMouseLeave={() => setTip(false)}
        onFocus={() => setTip(true)}
        onBlur={() => setTip(false)}
        className={cn(
          "inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-dashed border-accent px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
          ready ? "text-fg hover:bg-bg-subtle" : "cursor-not-allowed text-fg-subtle opacity-70",
        )}
      >
        <span
          aria-hidden="true"
          className="rounded-sm bg-accent px-1 text-[10px] font-semibold uppercase tracking-wide text-white"
        >
          Demo only
        </span>
        Change the scenario
      </button>
      {ready ? null : (
        <span
          id={tipId}
          role="tooltip"
          data-testid="demo-scenario-tip"
          data-open={tip ? "true" : "false"}
          className={cn(
            "absolute right-0 top-full z-50 mt-1 w-max max-w-[16rem] rounded-md border border-border bg-bg-elevated px-2 py-1 text-xs text-fg shadow-md",
            tip ? "block" : "sr-only",
          )}
        >
          {LOCKED_TEXT}
        </span>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="demo-scenario-dialog">
          <DialogHeader>
            <DialogTitle>Change the scenario</DialogTitle>
            <DialogDescription>
              See how Tarii answers a real change on a team like yours.
            </DialogDescription>
          </DialogHeader>
          <p
            className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-fg"
            data-testid="demo-scenario-reset-note"
            role="note"
          >
            <RotateCcw aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-warning" />A new
            scenario starts over from the original roster. Your solve and anything you published are
            cleared.
          </p>
          <DemoChangePicker
            // Re-keyed so the radio starts on what the team shows now.
            key={current ?? "base"}
            changes={changes}
            current={current}
            busy={apply.isPending}
            error={apply.error ? apply.error.detail : null}
            onApply={(variant) => {
              // The old card and the phone go at once, while the team is rebuilt.
              setDemoGuideScenarioPending(true);
              setOpen(false);
              apply.mutate(variant, {
                onError: () => {
                  setDemoGuideScenarioPending(false);
                  setOpen(true);
                },
              });
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
